import React, { useState, useEffect } from 'react';
import { ResponsivePie } from '@nivo/pie';

interface DashboardPieChartProps {
    data: Array<{
        id: string;
        label: string;
        value: number;
        color?: string; // Optional color per data item
        facilityId?: number; // Optional facility ID for navigation
    }>;
    startAngle?: number;
    endAngle?: number;
    transitionMode?: 'startAngle' | 'endAngle';
    innerRadius?: number;
    height?: number;
    legendConfig?: 'default' | 'right' | 'left' | 'none' | any;
    colors?: any; // Color scheme or function
    valueFormat?: (value: number) => string; // Custom value formatter
    showNoDataMessage?: boolean; // Show "no data" overlay when true
    enableArcLinkLabels?: boolean; // Show/hide pointer labels with lines
    onSliceClick?: (data: { id: string; label: string; value: number; facilityId?: number }) => void; // Click handler
}

// Helper function to calculate color luminance and determine text color
const getTextColorForBackground = (backgroundColor: string): string => {
    // Convert hex to RGB
    let hex = backgroundColor.replace('#', '');
    
    // Handle 3-digit hex codes
    if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
    }
    
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate relative luminance using the formula from WCAG
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return white text for dark backgrounds, black for light backgrounds
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

export const DashboardPieChart: React.FC<DashboardPieChartProps> = ({
    data,
    startAngle = 0,
    endAngle = 360,
    transitionMode = 'startAngle',
    innerRadius = 0.5,
    height,
    legendConfig = 'default',
    colors = { scheme: 'pastel1' },
    valueFormat,
    showNoDataMessage = false,
    enableArcLinkLabels = false,
    onSliceClick
}) => {
    const [animatedData, setAnimatedData] = useState<typeof data>([]);

    // Trigger animation after component mounts
    useEffect(() => {
        const timer = setTimeout(() => {
            setAnimatedData(data);
        }, 100);
        return () => clearTimeout(timer);
    }, [data]);
    const defaultMotionConfig = {
        mass: 1,
        tension: 170,
        friction: 26,
        clamp: false,
        precision: 0.01,
        velocity: 0
    };

    const getLegendConfig = () => {
        if (legendConfig === 'none') {
            return [];
        }
        
        if (legendConfig === 'right') {
            return [
                {
                    anchor: 'top-right' as const,
                    direction: 'column' as const,
                    justify: false,
                    translateX: 100,
                    translateY: 0,
                    itemsSpacing: 2,
                    itemWidth: 150,
                    itemHeight: 16,
                    itemTextColor: '#333',
                    itemDirection: 'left-to-right' as const,
                    itemOpacity: 1,
                    symbolSize: 14,
                    symbolShape: 'circle' as const,
                    effects: [
                        {
                            on: 'hover' as const,
                            style: {
                                itemTextColor: '#000'
                            }
                        }
                    ]
                }
            ];
        }
        
        if (legendConfig === 'left') {
            return [
                {
                    anchor: 'bottom-left' as const,
                    direction: 'column' as const,
                    justify: false,
                    translateX: -120,
                    translateY: 0,
                    itemsSpacing: 2,
                    itemWidth: 150,
                    itemHeight: 16,
                    itemTextColor: '#333',
                    itemDirection: 'left-to-right' as const,
                    itemOpacity: 1,
                    symbolSize: 14,
                    symbolShape: 'circle' as const,
                    effects: [
                        {
                            on: 'hover' as const,
                            style: {
                                itemTextColor: '#000'
                            }
                        }
                    ]
                }
            ];
        }
        
        if (typeof legendConfig === 'object') {
            return [legendConfig];
        }
        
        // Default configuration (good for few items)
        return [
            {
                anchor: 'bottom' as const,
                direction: 'row' as const,
                justify: false,
                translateX: 0,
                translateY: 56,
                itemsSpacing: 0,
                itemWidth: 100,
                itemHeight: 18,
                itemTextColor: '#999',
                itemDirection: 'left-to-right' as const,
                itemOpacity: 1,
                symbolSize: 18,
                symbolShape: 'circle' as const,
                effects: [
                    {
                        on: 'hover' as const,
                        style: {
                            itemTextColor: '#000'
                        }
                    }
                ]
            }
        ];
    };

    const containerStyle = { 
        height: height ? `${height}px` : '300px',
        width: '100%',
        position: 'relative' as const
    };

    // Add cursor pointer style when onSliceClick is provided
    const pieChartStyle = onSliceClick ? {
        cursor: 'pointer'
    } : {};

    return (
        <div style={containerStyle} className={onSliceClick ? 'pie-chart-clickable' : ''}>
            <style>
                {onSliceClick && `
                    .pie-chart-clickable svg {
                        cursor: pointer;
                    }
                    .pie-chart-clickable g[data-testid="arc"] {
                        cursor: pointer !important;
                    }
                    .pie-chart-clickable path {
                        cursor: pointer !important;
                    }
                `}
            </style>
            {showNoDataMessage && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(248, 249, 250, 0.95)',
                    zIndex: 10,
                    borderRadius: '8px'
                }}>
                    <div style={{
                        textAlign: 'center',
                        color: '#6c757d',
                        fontSize: '14px'
                    }}>
                        <div style={{ fontSize: '24px', marginBottom: '8px' }}>📊</div>
                        <div><strong>No Data Available</strong></div>
                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                            Try adjusting the date filter
                        </div>
                    </div>
                </div>
            )}
            <ResponsivePie
                data={animatedData}
                margin={{ top: 20, right: 115, bottom: 20, left: 10 }}
                innerRadius={innerRadius}
                padAngle={0.7}
                cornerRadius={3}
                activeOuterRadiusOffset={8}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                colors={colors}
                enableArcLinkLabels={enableArcLinkLabels}
                arcLinkLabelsSkipAngle={enableArcLinkLabels ? 10 : 360}
                arcLinkLabelsTextColor="#333333"
                arcLinkLabelsThickness={2}
                arcLinkLabelsColor={{ from: 'color' }}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor={(slice) => getTextColorForBackground(slice.color)}
                valueFormat={valueFormat}
                startAngle={startAngle}
                endAngle={endAngle}
                animate={true}
                motionConfig={defaultMotionConfig}
                transitionMode={transitionMode}
                defs={[
                    {
                        id: 'dots',
                        type: 'patternDots',
                        background: 'inherit',
                        color: 'rgba(255, 255, 255, 0.3)',
                        size: 4,
                        padding: 1,
                        stagger: true
                    },
                    {
                        id: 'lines',
                        type: 'patternLines',
                        background: 'inherit',
                        color: 'rgba(255, 255, 255, 0.3)',
                        rotation: -45,
                        lineWidth: 6,
                        spacing: 10
                    }
                ]}
                legends={getLegendConfig()}
                onClick={onSliceClick ? (slice) => onSliceClick({
                    id: slice.data.id,
                    label: slice.data.label,
                    value: slice.data.value,
                    facilityId: slice.data.facilityId
                }) : undefined}
            />
        </div>
    );
}; 